import React, { useState, useRef, useEffect, useCallback } from "react";
import "./OrientationInput.css";

const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

const getDirectionFromAngle = (angle) => {
  const index = Math.round(angle / 45) % 8;
  return directions[index];
};

export default function OrientationInput({ nextStep, updateFormData }) {
  const [angle, setAngle] = useState(0);
  const [selectedDirection, setSelectedDirection] = useState("N");
  const compassRef = useRef(null);

  const handleInteraction = useCallback((clientX, clientY) => {
    const rect = compassRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    const radians = Math.atan2(dy, dx);
    let degrees = radians * (180 / Math.PI);
    degrees = (degrees + 450) % 360;

    setAngle(degrees);
    setSelectedDirection(getDirectionFromAngle(degrees));
  }, []);

  const handleMouseMove = (e) => {
    handleInteraction(e.clientX, e.clientY);
  };

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      e.preventDefault();
      handleInteraction(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [handleInteraction]);

  const handleClick = () => {
    updateFormData({ orientation: selectedDirection });
    nextStep();
  };

  useEffect(() => {
    const compassEl = compassRef.current;
    if (!compassEl) return;

    compassEl.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      compassEl.removeEventListener("touchmove", handleTouchMove);
    };
  }, [handleTouchMove]);

  return (
    <div className="orientation-container">
      <div className="orientation-card">
        <h2>Select the sunniest direction your roof faces</h2>
        <p>Move the compass needle to tell us the orientation of your sunniest roof. There's no need to click the compass, just move it to the correct direction and move your cursor away from the compass before clicking continue.</p>
        <div className="compass-wrapper">
          <svg
            tabIndex={-1}
            viewBox="0 0 200 200"
            className="compass-svg"
            ref={compassRef}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            style={{ touchAction: "none" }}
          >
            {/* Compass Circle */}
            <circle cx="100" cy="100" r="90" stroke="#ccc" strokeWidth="4" fill="white" />

            {/* Tick Marks */}
            {Array.from({ length: 72 }).map((_, i) => {
              const angle = (i * 5 * Math.PI) / 180;
              const inner = i % 6 === 0 ? 75 : 80;
              const x1 = 100 + inner * Math.cos(angle);
              const y1 = 100 + inner * Math.sin(angle);
              const x2 = 100 + 90 * Math.cos(angle);
              const y2 = 100 + 90 * Math.sin(angle);
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#888"
                  strokeWidth={i % 6 === 0 ? 2 : 1}
                />
              );
            })}

            {/* Direction Labels */}
            {directions.map((dir, i) => {
              const angle = ((i * 45 - 90) * Math.PI) / 180;
              const x = 100 + 60 * Math.cos(angle);
              const y = 100 + 60 * Math.sin(angle) + 4;
              return (
                <text
                  key={dir}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fontSize="12"
                  fontWeight={dir === "N" ? "bold" : "normal"}
                >
                  {dir}
                </text>
              );
            })}

            {/* Needle */}
            <g transform={`rotate(${angle} 100 100)`}>
              <polygon points="100,20 96,100 104,100" fill="#00B2A9" />
            </g>

            {/* Center Dot */}
            <circle cx="100" cy="100" r="4" fill="#333" />
          </svg>
        </div>

        <p className="orientation-label">
          Selected direction: <strong>{selectedDirection}</strong>
        </p>
        <button className="continue-button" onClick={handleClick}>
          Continue
        </button>
      </div>
    </div>
  );
}
